import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ManageSearch as ManageSearchIcon } from '@frontierag/frontier-ui-library/icons';
import {
  APP_PRODUCT_TYPE,
  GROUPED_PRODUCTS_TYPES,
  LOAD_PRODUCTS_LIMIT,
  MAX_NUMBER_OF_PRODUCTS_PER_SUBGROUP,
} from 'appConstants';
import { CropSelectOption, GroupedProductSelect } from 'components';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useProducts } from 'contexts/Products';
import type { IProduct } from 'contexts/Products';
import isEqual from 'lodash/isEqual';

import type { IProductSelectProps } from './types';

const getOptionLabel = (product: string | IProduct) => {
  if (typeof product === 'string') return product;

  const { packagedItemDescription, packageDescription } = product;
  return `${packagedItemDescription} (${packageDescription})`;
};

const getCropOptionLabel = ({
  packagedItemDescription,
  packageDescription,
  packagedItemReference,
}: IProduct) => `${packagedItemDescription} (${packageDescription}) ${packagedItemReference} `;

export default function ProductSelect({
  disabled,
  location,
  selectedProductReference,
  textFieldProps,
  limitResult = MAX_NUMBER_OF_PRODUCTS_PER_SUBGROUP,
  subGroup,
  onChange = () => {},
  productType = APP_PRODUCT_TYPE.FERTILISER,
}: IProductSelectProps) {
  const { t } = useTranslation('ProductSelect');

  const {
    state,
    actions: { loadProducts },
    selectors: { selectProduct, selectIndexedProducts },
  } = useProducts();
  const indexedProducts = selectIndexedProducts(state);

  const { loading, loadedProductType, error, loadedParams } = state;

  const products = Object.values(indexedProducts); // Remove duplicates
  const currentSelectedProduct = selectProduct(state, selectedProductReference) || null;

  const filteredProducts = useMemo(() => {
    if (!subGroup) return products;

    return products.filter(
      (currentProduct) =>
        currentProduct.subGroupDescription === subGroup || currentProduct.isRecent,
    );
  }, [products, subGroup]);

  useEffect(() => {
    const params = { limit: LOAD_PRODUCTS_LIMIT, location };
    if (
      location &&
      productType === APP_PRODUCT_TYPE.FERTILISER &&
      (loadedProductType !== productType || !isEqual(params, loadedParams)) &&
      !loading &&
      !error
    ) {
      loadProducts(params, productType);
    }
  }, [error, loadProducts, loadedParams, loadedProductType, loading, location, productType]);

  useEffect(() => {
    if (
      productType === APP_PRODUCT_TYPE.CROP &&
      loadedProductType !== productType &&
      !loading &&
      !error
    ) {
      loadProducts({ limit: LOAD_PRODUCTS_LIMIT }, productType);
    }
  }, [error, loadProducts, loadedProductType, loading, location, productType]);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <DynamicAutocomplete
      disabled={disabled}
      textFieldProps={{
        label: t('labels.product'),
        placeholder: t('placeholders.searchForProduct'),
        ...textFieldProps,
      }}
      startAdornmentIcon={<ManageSearchIcon />}
      noOptionsText={t('labels.noOptions')}
      value={currentSelectedProduct}
      options={filteredProducts}
      getOptionLabel={getOptionLabel}
      onChange={handleOnChange}
      loading={loading}
      // @ts-expect-error - groupBy accepts a string, but isRecent is a boolean
      groupBy={(option) => option.isRecent}
      displayPopupIcon
      filterOptionsProps={{
        limit: limitResult,
        stringify: (option) =>
          APP_PRODUCT_TYPE.CROP ? getCropOptionLabel(option) : getOptionLabel(option),
      }}
      {...(productType === APP_PRODUCT_TYPE.CROP && {
        renderOption: (
          props,
          { packagedItemDescription, packageDescription, packagedItemReference }: IProduct,
        ) => (
          <CropSelectOption
            key={props.id}
            props={props}
            itemDescription={`${packagedItemDescription} (${packageDescription})`}
            itemReference={packagedItemReference}
          />
        ),
      })}
      renderGroup={(params) => (
        <GroupedProductSelect
          key={params.key}
          group={params.group ? GROUPED_PRODUCTS_TYPES.LAST_USED : GROUPED_PRODUCTS_TYPES.LIMITED}
          transNamespace="ProductSelect"
        >
          {params.children}
        </GroupedProductSelect>
      )}
      isOptionEqualToValue={(option, value) => {
        return option.packagedItemReference === value.packagedItemReference;
      }}
    />
  );
}
